/**
 * BialgebraExplainer — progressive-disclosure explainer for bialgebras,
 * distributive laws, and hylomorphisms.
 *
 * A bialgebra carries both an F-algebra and a G-coalgebra; coherence is
 * mediated by a distributive law λ : F ∘ G ⇒ G ∘ F. CSK's INV.CONSISTENCY
 * uses a special bialgebra to package "establish" and "preserve" into one
 * record (`InvariantDualPair` in PR #1544); when λ does not exist, the
 * compose-then-observe trace becomes structurally inexpressible.
 *
 * Acceptance criteria (issue #1552):
 *   - F-G-bialgebra pilot                       ✓ formal-tier square
 *   - Distributive law + bialgebra coherence    ✓ formal-tier `DistributiveLawDiagram`
 *   - Hylomorphism as `cata α ∘ ana γ`          ✓ formal-tier `HylomorphismDiagram`
 *   - Failure-vs-success distributive-law modes ✓ `ModeToggle` over `DistributiveLawDiagram`
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { DUAL_PAIR_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  BialgebraDiagram,
  CalloutBox,
  DistributiveLawDiagram,
  ExplainerCard,
  HylomorphismDiagram,
  ModeToggle,
  WorkedExample,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = DUAL_PAIR_COLOR["INV.CONSISTENCY"].color;
const ALGEBRA_ACCENT = THEOREM_COLOR["INV.ESTABLISH"];
const COALGEBRA_ACCENT = THEOREM_COLOR["INV.PRESERVE"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-bialgebra",
  kind: "Categorical structure",
  title: "Bialgebra",
  subtitle: "(A, α : F(A) → A, γ : A → G(A)) + distributive law λ : F∘G ⇒ G∘F",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "INV.CONSISTENCY (I1+I2)", href: "#explainer-inv-consistency", accent: ACCENT },
  { label: "F-Algebra", href: "#explainer-falgebra", accent: ALGEBRA_ACCENT },
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra", accent: COALGEBRA_ACCENT },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "ALWAYS (Al)", href: "#explainer-always" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>bialgebra</strong> is one carrier wearing two hats — an algebra <em>and</em> a
      coalgebra — kept consistent by a distributive law <MathInline>{tex`\lambda`}</MathInline>. CSK
      uses bialgebras to guarantee that "what was built" and "what can be observed" never disagree,
      and exploits the <em>absence</em> of <MathInline>{tex`\lambda`}</MathInline> as a structural
      security guarantee.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of an ETL pipeline. The <strong>builder</strong> (an F-algebra) ingests source events
        and constructs the warehouse row. The <strong>auditor</strong> (a G-coalgebra) reads the
        warehouse row and emits an immutable audit observation. Each is well formed on its own. The
        hard question is whether they cooperate:{" "}
        <em>
          can you observe what you just built, and rebuild what you observed, without losing
          information?
        </em>
      </p>
      <p>
        The distributive law{" "}
        <MathInline>{tex`\lambda : F \circ G \Rightarrow G \circ F`}</MathInline> is the answer.
        When it exists, building-then-observing and observing-then-building are interchangeable up
        to <MathInline>{tex`\lambda`}</MathInline> — the pipeline is <em>coherent</em>. When it does
        not, the two operations refuse to compose at all, and that absence is exactly the safety
        guarantee CSK extracts from the structure.
      </p>
      <CalloutBox eyebrow="The one-line takeaway" accent={ACCENT}>
        <em>
          A bialgebra makes "compose-then-observe = observe-then-compose" a typed obligation, not a
          code review checklist.
        </em>{" "}
        When the distributive law cannot exist, the unsafe composition has no closed term to name it
        — no runtime guard required.
      </CalloutBox>
    </>
  ),
  formal: (
    <>
      <p>
        Given endofunctors <MathInline>{tex`F, G : C \to C`}</MathInline>, an{" "}
        <strong>F-G bialgebra</strong> is a triple{" "}
        <MathInline>{tex`(A, \alpha, \gamma)`}</MathInline> with an F-algebra structure{" "}
        <MathInline>{tex`\alpha : F(A) \to A`}</MathInline> and a G-coalgebra structure{" "}
        <MathInline>{tex`\gamma : A \to G(A)`}</MathInline>, made coherent by a distributive law{" "}
        <MathInline>{tex`\lambda : F \circ G \Rightarrow G \circ F`}</MathInline>. The coherence
        square:
      </p>
      <BialgebraDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.75rem" }}>
        The <strong>distributive law</strong> is the natural transformation that ties{" "}
        <MathInline>{tex`\alpha`}</MathInline> and <MathInline>{tex`\gamma`}</MathInline> together.
        Whether it exists is a question with teeth — CSK uses both regimes:
      </p>
      <ModeToggle<"exists" | "missing">
        options={[
          { mode: "exists", label: "λ exists" },
          { mode: "missing", label: "λ missing" },
        ]}
        defaultMode="exists"
        accent={ACCENT}
        ariaLabel="Distributive law existence toggle"
        caption={(m) =>
          m === "exists"
            ? "Builders and observers commute up to λ — the bialgebra is coherent."
            : "No closed term names the swap — the unsafe compose-then-observe trace is structurally inexpressible."
        }
      >
        {(m) => <DistributiveLawDiagram mode={m} accent={ACCENT} />}
      </ModeToggle>
      <p style={{ marginTop: "0.75rem" }}>
        When <MathInline>{tex`\lambda`}</MathInline> does exist, the <strong>hylomorphism</strong>{" "}
        <MathInline>{tex`\mathrm{hylo}\ \alpha\ \gamma = \mathrm{cata}\ \alpha \circ \mathrm{ana}\ \gamma : \mu F \to \nu G`}</MathInline>{" "}
        fuses unfold and fold into a single pass that never materializes the intermediate structure:
      </p>
      <HylomorphismDiagram
        accent={ACCENT}
        algebraAccent={ALGEBRA_ACCENT}
        coalgebraAccent={COALGEBRA_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        For an <MathInline>{tex`F = G`}</MathInline> bialgebra with the identity distributive law{" "}
        <MathInline>{tex`\lambda = \mathrm{id} : F \circ F \Rightarrow F \circ F`}</MathInline>, the
        canonical <MathInline>{tex`\mu F \to \nu F`}</MathInline> hylomorphism is the joint witness
        CSK uses for INV.CONSISTENCY: the carrier is both the smallest F-closed type (initial
        algebra, builder side) and the largest F-closed type (terminal coalgebra, observer side),
        and the unique bridge between them certifies that what was built is coherently observable —
        no information escapes in transit.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        CSK does not demand an arbitrary distributive law. It demands the <em>specific</em>{" "}
        distributive law for the invariant functor. PR #1544's <code>InvariantDualPair</code>{" "}
        packages I1 + I2 into exactly this bialgebra; <code>HylomorphismComposition</code> connects
        two consistent machines into one without leaking an intermediate representation.
      </p>
      <WorkedExample
        eyebrow="Real-world story"
        title="An ETL pipeline whose audit log never contradicts its rows"
        accent={ACCENT}
        scenario={
          <>
            A nightly ETL job ingests raw events (the F-algebra:{" "}
            <em>build a warehouse row from its constituent events</em>) and continuously feeds an
            audit stream (the G-coalgebra: <em>emit one immutable audit record per row read</em>).
            The distributive law <MathInline>{tex`\lambda`}</MathInline> says: it does not matter
            whether you build the row first and then audit, or audit the row's source events and
            then build — the result is the same up to <MathInline>{tex`\lambda`}</MathInline>.
          </>
        }
        withoutStructure={
          <>
            Without <MathInline>{tex`\lambda`}</MathInline>, builders and auditors are two disjoint
            pipelines maintained by two teams. A schema migration on the builder side silently
            desynchronizes the auditor; the next compliance review finds rows whose audit trail does
            not match. By the time anyone notices, the only fix is a forensic reconstruction.
          </>
        }
        withStructure={
          <>
            With the bialgebra coherence law mechanized, a schema change that breaks{" "}
            <MathInline>{tex`\lambda`}</MathInline> fails to compile. The contradictory audit trail
            is <em>structurally inexpressible</em>: there is no closed term whose two paths through
            the square disagree.
          </>
        }
      />
      <p style={{ marginTop: "0.75rem" }}>
        When a candidate "establish + preserve" pair fails to admit{" "}
        <MathInline>{tex`\lambda`}</MathInline>, the joint INV.CONSISTENCY witness is{" "}
        <em>structurally inexpressible</em> — there is simply no closed term that names the alleged
        composition. That is the security-by-construction outcome: the unsafe trace is removed from
        the language of legal programs, not from a runtime filter.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras (§8 Bialgebras and Bimonads, lines 605–654)",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "F-bialgebras, distributive laws, and bimonads.",
    },
    {
      label: "InvariantDualPair (Agda, PR #1544 Contrib)",
      path: "agda/Contrib/StructuralInvariantSafetyGuarantees/InvariantDualPair.agda",
      note: "Packages I1 + I2 into a single bialgebra record; HylomorphismComposition stacks two consistent machines.",
    },
    {
      label: "Hylomorphisms (Agda)",
      path: "agda/MathLibCore/Lemmas/RecursionSchemes/Hylomorphisms.agda",
      note: "Algebra-coalgebra fusion used by INV.CONSISTENCY's joint witness.",
    },
    {
      label: "Joint INV.CONSISTENCY neutralization (Agda)",
      path: "agda/Kernel/INV/CONSISTENCY/Neutralization.agda",
      note: "Mechanized proof that the joint witness neutralizes the I1∧¬I2 and ¬I1∧I2 attack classes.",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Lands InvariantDualPair, HylomorphismComposition, and Chapter 04 (INV.CONSISTENCY) in `agda/Contrib/StructuralInvariantSafetyGuarantees/` and `theory/manuscripts/contrib/`.",
    },
  ],
};

export function BialgebraExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<BialgebraDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
